import React, { useState } from "react";
import Icon from "../Icon";
import SplitText from "./SplitText";
import Reveal from "../Reveal";
import SectionIntro from "./SectionIntro";
import { PURPOSE } from "../../data/about";

/**
 * Two ideas that refuse to be the same size.
 *
 * Mission and vision are a pair, but they are not equivalent — one is what the
 * company does now, the other is where it is going — so the section is built
 * as a split that responds to attention: whichever half the pointer is over
 * takes more of the width, and the other steps back. Nothing is hidden by it;
 * the layout just stops pretending the two are interchangeable.
 *
 * Below `lg` the halves stack at full width and the growth is dropped: there
 * is no hover to respond to, and a panel that resizes on tap reads as a bug.
 */

function Half({ side, panel, active, dim, onEnter, onLeave, tone }) {
  return (
    <div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      /* flex-grow is the only thing animating, and only from `lg` up where a
         pointer exists. `lg:basis-0` keeps the two even before any hover —
         and is scoped to `lg` on purpose: on mobile the container is a
         column, where a zero basis would size the panel from its minimum
         height and clip anything taller against the overflow rule. */
      className={`group relative isolate flex min-h-[28rem] flex-col justify-between overflow-hidden p-9 transition-[flex-grow] duration-[900ms] [transition-timing-function:var(--mw-glide)] sm:p-12 lg:min-h-[34rem] lg:basis-0 lg:p-14 ${
        active ? "lg:grow-[1.12]" : dim ? "lg:grow-[0.88]" : "lg:grow"
      }`}
    >
      {/* Each half carries its own light, so the two read as different places
          rather than one band split by a rule. */}
      <div className="absolute inset-0 -z-10" aria-hidden="true">
        <div className={`absolute inset-0 ${tone.base}`} />
        <div
          className={`mw-aurora absolute h-[34rem] w-[34rem] rounded-full blur-[120px] ${tone.glow}`}
          style={{ animationDelay: side === "vision" ? "-13s" : "0s" }}
        />
        <div className="mw-grain absolute inset-0 opacity-[0.13] mix-blend-overlay" />
        {/* Brightens under the pointer — the panel acknowledging attention
            without moving anything the reader is trying to read. */}
        <div
          className={`absolute inset-0 bg-white/0 transition-colors duration-[900ms] ${
            active ? "bg-white/[0.045]" : ""
          }`}
        />
      </div>

      <div>
        <div className="flex items-center gap-4">
          <span className="mw-eyebrow text-[#FBCFE8]">{panel.kicker}</span>
          <span
            className={`h-px bg-white/25 transition-all duration-[900ms] ${
              active ? "w-16" : "w-8"
            }`}
            aria-hidden="true"
          />
        </div>

        <SplitText
          as="h3"
          text={panel.title}
          className="mw-h2 mt-6 text-white"
          stagger={70}
        />

        <p className="mw-lede mt-6 max-w-md text-[#DCC9F2]">{panel.body}</p>
      </div>

      <ul className="mt-12 space-y-px">
        {panel.points.map((point, i) => (
          <li
            key={point}
            className="flex items-center gap-4 border-t border-white/12 py-4 transition-colors duration-500 last:border-b"
          >
            <Icon
              name="check"
              className={`h-4 w-4 shrink-0 transition-colors duration-500 ${
                active ? "text-[#FBCFE8]" : "text-white/35"
              }`}
            />
            <span className="text-[15px] font-medium text-white/85">{point}</span>
            <span className="mw-eyebrow ml-auto text-white/30">
              {String(i + 1).padStart(2, "0")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MissionVision() {
  const [hovered, setHovered] = useState(null);

  return (
    <section id="purpose" className="scroll-mt-24 bg-[#0B0518]">
      <div className="mx-auto max-w-7xl px-6 pt-24 md:px-10 lg:pt-32">
        <Reveal>
          <SectionIntro index={PURPOSE.index} label={PURPOSE.label} tone="dark" />
        </Reveal>
      </div>

      {/* Full bleed and butted together: the seam between the two halves is
          the composition, so there is no gap and no card. */}
      <div className="mt-12 flex flex-col lg:mt-14 lg:flex-row">
        <Half
          side="mission"
          panel={PURPOSE.mission}
          active={hovered === "mission"}
          dim={hovered === "vision"}
          onEnter={() => setHovered("mission")}
          onLeave={() => setHovered(null)}
          tone={{
            base: "bg-[linear-gradient(155deg,#3B0764_0%,#1A0430_60%,#0B0518_100%)]",
            glow: "-left-24 -top-32 bg-[#7C3AED]/30"
          }}
        />

        <div
          className="h-px w-full bg-white/12 lg:h-auto lg:w-px"
          aria-hidden="true"
        />

        <Half
          side="vision"
          panel={PURPOSE.vision}
          active={hovered === "vision"}
          dim={hovered === "mission"}
          onEnter={() => setHovered("vision")}
          onLeave={() => setHovered(null)}
          tone={{
            base: "bg-[linear-gradient(155deg,#12021D_0%,#2E1065_55%,#4C087D_100%)]",
            glow: "-bottom-32 -right-24 bg-[#A855F7]/25"
          }}
        />
      </div>
    </section>
  );
}

export default MissionVision;
